import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  timeout
} from 'rxjs';
import { ConfigurationService } from '@sondermind/configuration';
import { camelCaseTransformer } from '@sondermind/utilities/tools';
import { StrapiContentTypes } from './constants/strapi-content-types';
import {
  StrapiAttributes,
  StrapiRes,
  StrapiResponse,
  IStrapiFooterNavigation,
  IStrapiLegalNavigation,
  IStrapiSocialNavigation,
  IStrapiGlobalText,
  IProviderMenu,
  IPublicFooterNavigationBundle
} from './constants/strapi-dto';
import {
  FALLBACK_FOOTER_NAVIGATION,
  FALLBACK_LEGAL_NAVIGATION,
  FALLBACK_SOCIAL_NAVIGATION,
  FALLBACK_GLOBAL_TEXT,
  PUBLIC_FOOTER_NAVIGATION_DEFAULT_BUNDLE
} from './constants/strapi-fallback-data';

const STRAPI_HTTP_TIMEOUT_MS = 5000;
const SHARE_REPLAY = { bufferSize: 1, refCount: true } as const;

@Injectable({
  providedIn: 'root'
})
export class StrapiService {
  private apiUrl = `${this.configService.env.svcBase}/core/strapi`;

  // Cache for navigation content
  private footerNavigation$: Observable<IStrapiFooterNavigation> | null = null;
  private legalNavigation$: Observable<IStrapiLegalNavigation> | null = null;
  private socialNavigation$: Observable<IStrapiSocialNavigation> | null = null;
  private globalText$: Observable<IStrapiGlobalText> | null = null;
  private providers$: Observable<IProviderMenu[]> | null = null;
  private footerNavigationBundle$: Observable<IPublicFooterNavigationBundle> | null = null;

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigurationService,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  getContent(
    contentType: StrapiContentTypes,
    filterKey: string,
    filterValues: string[],
    populate?: string
  ): Observable<StrapiAttributes[]> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('filter_key', filterKey)
      .set('page_size', '50');

    if (populate) {
      params = params.set('populate', populate);
    }

    filterValues.forEach((value) => {
      params = params.append('filter_values[]', value);
    });

    return this.httpClient.get<StrapiRes>(this.apiUrl, { params })
      .pipe(
        timeout(STRAPI_HTTP_TIMEOUT_MS),
        map((res: StrapiRes) => {
          if (res?.data?.length) {
            return res.data.map((val) => camelCaseTransformer<StrapiAttributes>(val));
          }

          return [];
        }),
        shareReplay(SHARE_REPLAY)
      );
  }

  /**
   * Fetches content from Strapi.
   * Content like footer, legal, and social navigations.
   */
  getNavigationContent<T>(
    contentType: StrapiContentTypes,
    populate?: string
  ): Observable<T | null> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('page_size', '100');

    if (populate) {
      params = params.set('populate', populate);
    }

    return this.httpClient.get<StrapiResponse<T>>(this.apiUrl, { params })
      .pipe(
        timeout(STRAPI_HTTP_TIMEOUT_MS),
        map((res: StrapiResponse<T>) => {
          if (res?.data) {
            return camelCaseTransformer<T>(res.data);
          }
          return null;
        }),
        shareReplay(SHARE_REPLAY)
      );
  }

  /**
   * Public-footer Strapi bundle. On SSR: synchronous default (no HTTP). On browser: live CMS.
   */
  getPublicFooterNavigationBundle$(): Observable<IPublicFooterNavigationBundle> {
    if (!this.footerNavigationBundle$) {
      if (isPlatformServer(this.platformId)) {
        this.footerNavigationBundle$ = of(PUBLIC_FOOTER_NAVIGATION_DEFAULT_BUNDLE).pipe(
          shareReplay(SHARE_REPLAY)
        );
      } else {
        this.footerNavigationBundle$ = forkJoin({
          footer: this.getFooterNavigation$(),
          legal: this.getLegalNavigation$(),
          social: this.getSocialNavigation$(),
          globalText: this.getGlobalText$(),
          providers: this.getProviders$()
        }).pipe(shareReplay(SHARE_REPLAY));
      }
    }
    return this.footerNavigationBundle$;
  }

  /**
   * Gets footer navigation menus from Strapi.
   * Returns fallback data on error or empty response.
   */
  getFooterNavigation$(): Observable<IStrapiFooterNavigation> {
    if (!this.footerNavigation$) {
      this.footerNavigation$ = this.getNavigationContent<IStrapiFooterNavigation>(
        StrapiContentTypes.FOOTER_NAVIGATION,
        'menus'
      ).pipe(
        map((data) => data || FALLBACK_FOOTER_NAVIGATION),
        catchError(() => of(FALLBACK_FOOTER_NAVIGATION))
      );
    }
    return this.footerNavigation$;
  }

  /**
   * Gets legal navigation links from Strapi.
   * Returns fallback data on error or empty response.
   */
  getLegalNavigation$(): Observable<IStrapiLegalNavigation> {
    if (!this.legalNavigation$) {
      this.legalNavigation$ = this.getNavigationContent<IStrapiLegalNavigation>(
        StrapiContentTypes.LEGAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_LEGAL_NAVIGATION),
        catchError(() => of(FALLBACK_LEGAL_NAVIGATION))
      );
    }
    return this.legalNavigation$;
  }

  /**
   * Gets social navigation links from Strapi.
   * Returns fallback data on error or empty response.
   */
  getSocialNavigation$(): Observable<IStrapiSocialNavigation> {
    if (!this.socialNavigation$) {
      this.socialNavigation$ = this.getNavigationContent<IStrapiSocialNavigation>(
        StrapiContentTypes.SOCIAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_SOCIAL_NAVIGATION),
        catchError(() => of(FALLBACK_SOCIAL_NAVIGATION))
      );
    }
    return this.socialNavigation$;
  }

  /**
   * Gets global text from Strapi.
   * Returns fallback data on error or empty response.
   */
  getGlobalText$(): Observable<IStrapiGlobalText> {
    if (!this.globalText$) {
      this.globalText$ = this.getNavigationContent<IStrapiGlobalText>(
        StrapiContentTypes.GLOBAL_TEXT,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_GLOBAL_TEXT),
        catchError(() => of(FALLBACK_GLOBAL_TEXT))
      );
    }
    return this.globalText$;
  }

  // Fetch providers from Strapi
  getProviders$(): Observable<IProviderMenu[]> {
    if (!this.providers$) {
      this.providers$ = this.getNavigationContent(
        StrapiContentTypes.PROVIDERS,
        '*',
      ).pipe(
        map((data) => (Array.isArray(data) ? data : []) as IProviderMenu[]),
        catchError(() => of([]))
      );
    }

    return this.providers$;
  }
}
